import React from 'react';
import {
  areaColumns,
  groupColumns,
  paymentColumns,
  paymentMethodColumns,
  saleEmployeeColumns
} from '../../cols-definition';
import { Group, GroupType, HanThanhToan, KhuVuc, NhanVien, PhuongThucThanhToan, TaiKhoan } from '@/types/schemas';
import { accountSearchColumns } from '@/constants/search-columns';
import { SearchField, FormField } from '@/components/custom/erp';
import QUERY_KEYS from '@/constants/query-keys';
import { Label } from '@/components/ui/label';

interface GeneralInfoTabProps {
  formMode: 'add' | 'edit' | 'view';
  nhanVien: NhanVien | null;
  nhomKH1: Group | null;
  nhomKH2: Group | null;
  nhomKH3: Group | null;
  taiKhoan: TaiKhoan | null;
  khuVuc: KhuVuc | null;
  hanThanhToan: HanThanhToan | null;
  phuongThucThanhToan: PhuongThucThanhToan | null;
  setHanThanhToan: (value: HanThanhToan | null) => void;
  setNhomKH1: (value: Group | null) => void;
  setNhomKH2: (value: Group | null) => void;
  setNhomKH3: (value: Group | null) => void;
  setKhuVuc: (value: KhuVuc | null) => void;
  setNhanVien: (value: NhanVien | null) => void;
  setTaiKhoan: (value: TaiKhoan | null) => void;
  setPhuongThucThanhToan: (value: PhuongThucThanhToan | null) => void;
}

function GeneralInfoTab({
  formMode,
  nhanVien,
  nhomKH1,
  nhomKH2,
  nhomKH3,
  taiKhoan,
  khuVuc,
  hanThanhToan,
  phuongThucThanhToan,
  setKhuVuc,
  setNhomKH1,
  setNhomKH2,
  setNhomKH3,
  setNhanVien,
  setTaiKhoan,
  setHanThanhToan,
  setPhuongThucThanhToan
}: GeneralInfoTabProps) {
  return (
    <div className='p-4 md:p-6'>
      <div className='flex flex-col gap-y-4 md:gap-y-6'>
        <div className='space-y-2 md:space-y-2'>
          {/* First Row: Customer Code & Type */}
          <div className='flex items-center gap-2'>
            <div className='flex flex-col sm:flex-row sm:items-center'>
              <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Nhân viên bán hàng</Label>
              <SearchField<NhanVien>
                type='text'
                searchEndpoint={`/${QUERY_KEYS.NHAN_VIEN}/`}
                searchColumns={saleEmployeeColumns}
                dialogTitle='Nhân viên bán hàng'
                columnDisplay='ma_nhan_vien'
                displayRelatedField='ho_ten_nhan_vien'
                relatedFieldValue={nhanVien?.ho_ten_nhan_vien}
                disabled={formMode === 'view'}
                onRowSelection={setNhanVien}
                value={nhanVien?.ma_nhan_vien}
              />
            </div>
          </div>

          {/* Second Row: Account Codes */}
          <div className='flex items-center gap-2'>
            <div className='flex flex-col sm:flex-row sm:items-center'>
              <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Tài khoản ngầm định</Label>
              <SearchField<TaiKhoan>
                type='text'
                searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
                searchColumns={accountSearchColumns}
                disabled={formMode === 'view'}
                dialogTitle='Tài khoản ngầm định'
                value={taiKhoan?.code || ''}
                relatedFieldValue={taiKhoan?.name}
                onRowSelection={setTaiKhoan}
              />
            </div>
          </div>

          <div className='flex flex-col sm:flex-row sm:items-center'>
            <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Mã th.toán công nợ</Label>
            <SearchField<HanThanhToan>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.HAN_THANH_TOAN}/`}
              searchColumns={paymentColumns}
              disabled={formMode === 'view'}
              dialogTitle='Mã th.toán công nợ'
              value={hanThanhToan?.ma_tt}
              relatedFieldValue={hanThanhToan?.ten_tt}
              onRowSelection={setHanThanhToan}
              className='w-100'
            />
          </div>

          {/* Third Row: Payment Terms */}
          <div className='flex items-center gap-2'>
            <div className='flex flex-col sm:flex-row sm:items-center'>
              <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Ph/th th.toán (HĐĐT)</Label>
              <SearchField<PhuongThucThanhToan>
                type='text'
                searchEndpoint={`/${QUERY_KEYS.PHUONG_THUC_THANH_TOAN}/`}
                searchColumns={paymentMethodColumns}
                disabled={formMode === 'view'}
                dialogTitle='Ph/th th.toán (HĐĐT)'
                value={phuongThucThanhToan?.ma_pttt}
                onRowSelection={setPhuongThucThanhToan}
                relatedFieldValue={phuongThucThanhToan?.ten_pttt}
              />
            </div>
          </div>

          <div className='flex flex-col sm:flex-row sm:items-center'>
            <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Giới hạn tiền nợ</Label>
            <FormField type='number' name='credit_limit' />
          </div>

          {/* Groups Section */}
          <div className='flex flex-col sm:flex-row sm:items-center'>
            <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Nhóm 1</Label>
            <SearchField<Group>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.NHOM}?loai_nhom=${GroupType.CUSTOMER1}`}
              searchColumns={groupColumns}
              disabled={formMode === 'view'}
              value={nhomKH1?.ma_nhom}
              relatedFieldValue={nhomKH1?.ten_phan_nhom}
              onRowSelection={setNhomKH1}
              className='w-100'
            />
          </div>

          <div className='flex flex-col sm:flex-row sm:items-center'>
            <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Nhóm 2</Label>
            <SearchField<Group>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.NHOM}?loai_nhom=${GroupType.CUSTOMER2}`}
              searchColumns={groupColumns}
              disabled={formMode === 'view'}
              value={nhomKH2?.ma_nhom}
              relatedFieldValue={nhomKH2?.ten_phan_nhom}
              onRowSelection={setNhomKH2}
              className='w-100'
            />
          </div>

          <div className='flex flex-col sm:flex-row sm:items-center'>
            <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Nhóm 3</Label>
            <SearchField<Group>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.NHOM}?loai_nhom=${GroupType.CUSTOMER3}`}
              searchColumns={groupColumns}
              disabled={formMode === 'view'}
              value={nhomKH3?.ma_nhom}
              onRowSelection={setNhomKH3}
              relatedFieldValue={nhomKH3?.ten_phan_nhom}
              className='w-100'
            />
          </div>

          <div className='flex flex-col sm:flex-row sm:items-center'>
            <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Khu vực</Label>
            <SearchField<KhuVuc>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.KHU_VUC}`}
              searchColumns={areaColumns}
              value={khuVuc?.rg_code}
              onRowSelection={setKhuVuc}
              relatedFieldValue={khuVuc?.rgname}
              className='w-100'
            />
          </div>

          {/* Contact Information */}
          <div className='flex items-center gap-2'>
            <div className='flex flex-col sm:flex-row sm:items-center'>
              <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Điện thoại</Label>
              <FormField type='text' name='phone' />
            </div>

            <div className='flex flex-col sm:flex-row sm:items-center'>
              <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Fax</Label>
              <FormField type='text' name='fax' />
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <div className='flex flex-col sm:flex-row sm:items-center'>
              <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Thư (Email)</Label>
              <FormField type='text' name='email' />
            </div>

            <div className='flex flex-col sm:flex-row sm:items-center'>
              <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Trang chủ (Website)</Label>
              <FormField type='text' name='website' />
            </div>
          </div>

          {/* Notes */}
          <div className='flex flex-col sm:flex-row sm:items-start'>
            <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Ghi chú</Label>
            <textarea
              name='notes'
              rows={2}
              className='w-1/2 rounded-md border-b border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
            />
          </div>

          {/* Status */}
          <div className='flex flex-col sm:flex-row sm:items-center'>
            <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Trạng thái</Label>
            <FormField
              type='select'
              name='status'
              className='w-[150px]'
              options={[
                { value: '1', label: '1. Còn sử dụng' },
                {
                  value: '0',
                  label: '0. Không sử dụng'
                }
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default GeneralInfoTab;
